import React from 'react';
import { Immutable } from 'nuclear-js';
import PropTypes from 'prop-types';
import CreateReactClass from 'create-react-class';

import CommonStrings from 'resources/common-strings';
import Reactor from 'data/reactor';
import RuntimeGetters from 'data/app-runtime/getters';
import RuntimeActions from 'data/app-runtime/actions';
import GrowthGoalUtils from '../utils';
import GrowthGoalsDataAccessUtils from '../data-access-utils';

import HelpBox from 'components/generic/help/index';
import UpcomingGrowthGoals from './upcoming-growth-goals';
import PastGrowthGoals from './past-growth-goals';
import GoalSettingSection from './goal-setting-section';
import Analytics from 'utils/analytics';
import CommonValues from 'resources/common-values';
import { deepCompare } from 'utils/react';

import DeleteGoalModal from './modals/delete-goal-modal';
import ActionPlanModal from './modals/action-plan-modal';
import GoalAlreadySetModal from './modals/goal-already-set-modal';
import GoalSettingFailedModal from './modals/goal-setting-failed';
import DataUtils from 'data/utils';

export default CreateReactClass({
    displayName: 'GrowthGoalsExpandedContainer',
    mixins: [Reactor.ReactMixin],

    propTypes: {
        currentGrowthGoals: PropTypes.instanceOf(Immutable.List),
        selectedTermNumber: PropTypes.number.isRequired,
        currentTerm: PropTypes.instanceOf(Immutable.Map),
        expandedModule: PropTypes.string,
        firstOpenTerm: PropTypes.instanceOf(Immutable.Map),
        goalSettingTerms: PropTypes.instanceOf(Immutable.List),
        growthGoalNormsForStudent: PropTypes.instanceOf(Immutable.Map),
        growthGoalNormsForStudentLoaded: PropTypes.bool,
        growthGoals: PropTypes.instanceOf(Immutable.List),
        growthProjections: PropTypes.instanceOf(Immutable.List),
        growthProjectionsLoaded: PropTypes.bool,
        keystoneTestEvent: PropTypes.object,
        linkingStudiesData: PropTypes.object,
        linkingStudiesLoaded: PropTypes.bool,
        maxTestEvent: PropTypes.instanceOf(Immutable.Map),
        pastGrowthGoals: PropTypes.instanceOf(Immutable.List),
        ritQuintiles: PropTypes.instanceOf(Immutable.Map),
        ritQuintilesLoaded: PropTypes.bool,
        showSaveDataPrompt: PropTypes.func.isRequired,
        studentBid: PropTypes.string.isRequired,
        studentTermData: PropTypes.instanceOf(Immutable.List).isRequired,
        subject: PropTypes.instanceOf(Immutable.Map).isRequired,
        terms: PropTypes.instanceOf(Immutable.List),
        testEvents: PropTypes.instanceOf(Immutable.List).isRequired,
        testToProjectFromForCurrentTerm: PropTypes.instanceOf(Immutable.Map),
    },

    getInitialState() {
        return {
            highlightedTermNumber: null,
            highlightedTermEditedYn: false,
            upcomingGoalsExpandedYn: true,
        };
    },

    getDataBindings() {
        return {
            unsavedDataState: RuntimeGetters.unsavedDataState /* Only used here */,
            dropdownTermNumber: RuntimeGetters.popoverTermNumber /* Only used here */,
        };
    },

    shouldComponentUpdate: function(nextProps, nextState) {
        const isLoaded =
            nextProps.growthGoalNormsForStudentLoaded &&
            nextProps.growthProjectionsLoaded &&
            nextProps.ritQuintilesLoaded;
        return isLoaded && (!deepCompare(nextProps, this.props) || !deepCompare(nextState, this.state));
    },

    getUpcomingGoalsId() {
        return `upcoming-goals-for-subject-${this.props.subject.get('bid')}`;
    },

    highlightTerm(termNumber, editedYn) {
        // expand upcoming goals if it isn't already, then scroll to it
        if (!this.state.upcomingGoalsExpandedYn) {
            this.toggleUpcomingGoals();
        }

        this.setState({
            highlightedTermNumber: termNumber,
            highlightedTermEditedYn: editedYn,
        });
    },

    unHighlightTerm() {
        this.setState({
            highlightedTermNumber: null,
        });
    },

    toggleUpcomingGoals() {
        if (this.state.upcomingGoalsExpandedYn) {
            Analytics.recordButtonClick(CommonValues.SectionIDs.growthGoals, 'upcoming-goals-toggle', 'open');
        } else {
            Analytics.recordButtonClick(CommonValues.SectionIDs.growthGoals, 'upcoming-goals-toggle', 'close');
        }
        this.setState({
            upcomingGoalsExpandedYn: !this.state.upcomingGoalsExpandedYn,
        });
    },

    deleteGoal(goal) {
        let deleteGoalType = 'delete-previous-goal';
        if (
            !!this.props.currentGrowthGoals &&
            this.props.currentGrowthGoals.find((t) => t.get('endTermNumber') === goal.get('endTermNumber'))
        ) {
            deleteGoalType = 'delete-upcoming-goal';
        }
        Analytics.recordButtonClick(CommonValues.SectionIDs.growthGoals, deleteGoalType);
        this.refs.deleteGoalModal
            .show(goal)
            .then(() => {
                GrowthGoalsDataAccessUtils.deleteGrowthGoal(
                    this.props.studentBid,
                    this.props.maxTestEvent.get('measurementScaleID'),
                    goal,
                    () => {
                        /* intentionally blank */
                    },
                    () => {
                        this.showGoalSettingFailedModal()
                            .then(() => {
                                /* do nothing */
                            })
                            .catch(() => {
                                /* do nothing */
                            });
                    }
                );
            })
            .catch(() => {
                /* intentionally empty */
            });
    },

    showGoalAlreadySetModal() {
        return this.refs.alreadySetModal.show();
    },

    showGoalSettingFailedModal() {
        return this.refs.goalSettingFailed.show();
    },

    showActionPlanModal(actionPlan, growthGoalBid) {
        return this.refs.actionPlanModal.show(actionPlan, growthGoalBid);
    },

    setDirtyDataFlag() {
        RuntimeActions.setUnsavedData(true);
    },

    clearDirtyDataFlag(modifier = null) {
        RuntimeActions.setUnsavedData(false, modifier);
    },

    onCollapse() {
        Analytics.recordButtonClick(CommonValues.SectionIDs.growthGoals, 'close-module', 'growth-goals-expanded');
        const collapseActions = () => {
            RuntimeActions.expandModule(null, null, true);
        };

        if (this.state.unsavedDataState.get('unsavedData')) {
            this.props
                .showSaveDataPrompt()
                .then(() => {
                    collapseActions();
                    this.clearDirtyDataFlag('gg-collapse');
                })
                .catch(() => {
                    /* do nothing */
                });
        } else {
            collapseActions();
        }
    },

    onTransitionEnd(event) {
        event.stopPropagation();
        Reactor.batch(() => {
            RuntimeActions.completeTransition();
            RuntimeActions.setAnimationIsRunning(false);
        });
    },

    helpText() {
        return (
            <div key="growth-goal-help-content">
                <p key="initial-text" className="help-box-p">
                    {CommonStrings.growthExpandedHelp.initialText}
                </p>
                <div key="help-link-section" className="help-link">
                    <strong key="help-read-more">{CommonStrings.growthExpandedHelp.readMoreText}</strong>
                    <a key="help-link" href={CommonStrings.growthExpandedHelp.helpLink} target="_blank">
                        {CommonStrings.growthExpandedHelp.helpText}
                    </a>
                </div>
            </div>
        );
    },

    renderHeader() {
        return (
            <div className="header-wrapper" key="growth-goal-header-container">
                <header key="growth-goal-header">
                    <h5>Growth Goals</h5>
                    <HelpBox
                        id={'growth-goal-expanded-help-box-header'}
                        appLevelCssClassName=""
                        helpContentsRenderer={this.helpText}
                        moduleName={CommonValues.SectionIDs.growthGoals}
                        helpLabel="growth-goals-expanded-help"
                    />
                </header>
                <span key="growth-goal-collapse" className="icon-collapse" onClick={this.onCollapse}>
                    {CommonStrings.collapse}
                </span>
            </div>
        );
    },

    renderModals() {
        const goalAlreadySetModal = (
            <GoalAlreadySetModal ref="alreadySetModal" key="goal-already-set" subject={this.props.subject} />
        );
        const goalSettingFailedModal = (
            <GoalSettingFailedModal ref="goalSettingFailed" key="goal-setting-failed" subject={this.props.subject} />
        );
        const deleteGoalModal = (
            <DeleteGoalModal key="delete-goal-modal" ref="deleteGoalModal" subject={this.props.subject} />
        );
        const actionPlanModal = <ActionPlanModal key="action-plan-modal" ref="actionPlanModal" />;

        return [goalAlreadySetModal, goalSettingFailedModal, deleteGoalModal, actionPlanModal];
    },

    render() {
        const header = this.renderHeader();
        const selectedTestEvent = DataUtils.findTestEventForTermAndSubject(
            this.props.testEvents,
            this.props.selectedTermNumber,
            this.props.subject.get('bid')
        );
        const measurementScaleID = selectedTestEvent
            ? selectedTestEvent.get('measurementScaleID')
            : this.props.subject.get('bid');
        const growthGoalNormsForSubject = this.props.growthGoalNormsForStudent
            ? this.props.growthGoalNormsForStudent.get(measurementScaleID, null)
            : null;
        const startingTerm = GrowthGoalUtils.findStartingTerm(this.props.studentTermData, this.props.maxTestEvent);
        const modals = this.renderModals();

        return (
            <div className="module growth-goals expanded" onTransitionEnd={this.onTransitionEnd}>
                {header}
                <UpcomingGrowthGoals
                    currentGrowthGoals={this.props.currentGrowthGoals}
                    highlightedTermNumber={this.state.highlightedTermNumber}
                    highlightedTermEditedYn={this.state.highlightedTermEditedYn}
                    expandedYn={this.state.upcomingGoalsExpandedYn}
                    toggle={this.toggleUpcomingGoals}
                    id={this.getUpcomingGoalsId()}
                    deleteGoal={this.deleteGoal}
                    showActionPlanModal={this.showActionPlanModal}
                    key="upcoming-growth-goals"
                />
                <GoalSettingSection
                    dropdownTermNumber={this.state.dropdownTermNumber}
                    growthGoalsExpanded={this.props.expandedModule === CommonValues.SectionIDs.growthGoals}
                    studentBid={this.props.studentBid}
                    subject={this.props.subject}
                    goalSettingTerms={this.props.goalSettingTerms}
                    growthGoals={this.props.growthGoals}
                    studentTermData={this.props.studentTermData}
                    growthGoalNorms={growthGoalNormsForSubject}
                    growthGoalNormsLoaded={this.props.growthGoalNormsForStudentLoaded}
                    growthProjections={this.props.growthProjections}
                    growthProjectionsLoaded={this.props.growthProjectionsLoaded}
                    ritQuintiles={this.props.ritQuintiles}
                    ritQuintilesLoaded={this.props.ritQuintilesLoaded}
                    maxTestEvent={this.props.maxTestEvent}
                    firstOpenTerm={this.props.firstOpenTerm}
                    startingTerm={startingTerm}
                    highlightTerm={this.highlightTerm}
                    unHighlightTerm={this.unHighlightTerm}
                    showGoalAlreadySetModal={this.showGoalAlreadySetModal}
                    showGoalSettingFailedModal={this.showGoalSettingFailedModal}
                    showSaveDataPrompt={this.props.showSaveDataPrompt}
                    setDirtyDataFlag={this.setDirtyDataFlag}
                    clearDirtyDataFlag={this.clearDirtyDataFlag}
                    unsavedDataState={this.state.unsavedDataState}
                    currentTerm={this.props.currentTerm}
                    testToProjectFromForCurrentTerm={this.props.testToProjectFromForCurrentTerm}
                    testEvents={this.props.testEvents}
                    key="goal-setting-section"
                    upcomingGoalsId={this.getUpcomingGoalsId()}
                    terms={this.props.terms}
                    linkingStudiesData={this.props.linkingStudiesData}
                    linkingStudiesLoaded={this.props.linkingStudiesLoaded}
                    keystoneTestEvent={this.props.keystoneTestEvent}
                />
                <PastGrowthGoals
                    currentGrowthGoals={this.props.currentGrowthGoals}
                    pastGrowthGoals={this.props.pastGrowthGoals}
                    deleteGoal={this.deleteGoal}
                    showActionPlanModal={this.showActionPlanModal}
                    key="past-growth-goals"
                />
                {modals}
            </div>
        );
    },
});
